import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { ModelBox } from "@/components/ModelBox";
import { useEffect } from "react";
import CanonicalTag from "@/components/CanonicalTag";
import { NeumorphicThemeProvider, useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import NeumorphicThemeToggle from "@/components/NeumorphicThemeToggle";
import { NeumorphicContainer } from "@/components/neumorphic";

const models = [{
  id: 1,
  name: "Gilmour",
  squareFootage: 378,
  beds: 0,
  baths: 1,
  widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
  images: {
    "2D": ["/lovable-uploads/0910924-d4cb-4b53-8131-e7364b762518.png"],
    "3D": ["/lovable-uploads/0114c63b-5361-41d4-8633-feac775819c0.png"],
    exterior: ["/lovable-uploads/415954b9-1a79-470f-bf8b-3f5981dc17da.png"],
    interior: []
  }
}, {
  id: 2,
  name: "Di Meola",
  squareFootage: 389,
  beds: 1,
  baths: 1,
  widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
  images: {
    "2D": ["/lovable-uploads/0402f4be-93a9-4e6f-83c9-f390ea2aa5b6.png"],
    "3D": ["/lovable-uploads/045c9713-fc7d-47ab-8f98-5ee5bdd0aeb5.png"],
    exterior: ["/lovable-uploads/09797886-664e-4aa9-b648-d23241116ddd.png"],
    interior: []
  }
}, {
  id: 3,
  name: "Satriani",
  squareFootage: 541,
  beds: 1,
  baths: 1,
  widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
  images: {
    "2D": ["/lovable-uploads/07742ba5-337c-4155-8432-cece1acdbe88.png"],
    "3D": ["/lovable-uploads/07dfc299-225b-4d3c-b4c3-7ae077c4b1ee.png"],
    exterior: ["/lovable-uploads/f55e0610-bfdd-4421-a78d-6077b3d8a540.png"],
    interior: []
  }
}, {
  id: 4,
  name: "Knopfler",
  squareFootage: 797,
  beds: 1,
  baths: 1,
  widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
  images: {
    "2D": ["/lovable-uploads/0a60f49b-a0b6-4dd4-9257-d06f9428f393.png"],
    "3D": ["/lovable-uploads/0b8f05f0-c562-4dc2-906f-07f942e98dc1.png"],
    exterior: ["/lovable-uploads/81450f32-921a-4836-9e5f-f88339547387.png"],
    interior: []
  }
}, {
  id: 5,
  name: "Moore",
  squareFootage: 815,
  beds: 1,
  baths: 1,
  widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
  images: {
    "2D": ["/lovable-uploads/0fb32522-99b0-4ad7-8f82-6664dad62226.png"],
    "3D": ["/lovable-uploads/104e2d5e-2f29-44da-859e-968bce9636e9.png"],
    exterior: ["/lovable-uploads/37873af0-0fc2-4ca9-8ea0-1049dd49a3de.png"],
    interior: []
  }
}];

const losAltosModels = [{
  id: 13,
  name: "LA Wee",
  squareFootage: 525,
  beds: 1,
  baths: 1,
  widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506005&miniplan=original",
  images: {
    "2D": ["/lovable-uploads/fe69ec00-a304-485a-9099-cab81cd51b2e.png"],
    "3D": ["/lovable-uploads/fe69ec00-a304-485a-9099-cab81cd51b2e.png"],
    exterior: ["/lovable-uploads/c129b40b-8432-491f-a1af-a47d817aeee4.png"],
    interior: []
  }
}, {
  id: 14,
  name: "LA Median",
  squareFootage: 854,
  beds: 1,
  baths: 1,
  widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506008&miniplan=original",
  images: {
    "2D": ["/lovable-uploads/1399ed1c-d18a-411f-9862-b927eb39dca5.png"],
    "3D": ["/lovable-uploads/1399ed1c-d18a-411f-9862-b927eb39dca5.png"],
    exterior: ["/lovable-uploads/b616a741-04ec-4dfd-82b8-6311f8a5d3b8.png"],
    interior: []
  }
}, {
  id: 16,
  name: "LA Grand",
  squareFootage: 1190,
  beds: 2,
  baths: 2,
  widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
  images: {
    "2D": ["/lovable-uploads/f880f594-ac6d-418c-992d-649410b53e13.png"],
    "3D": ["/lovable-uploads/f880f594-ac6d-418c-992d-649410b53e13.png"],
    exterior: ["/lovable-uploads/1125af17-6a9b-45a5-8c3a-f3a53e6daccd.png"],
    interior: []
  }
}];

const UDAModelsContent = () => {
  const { theme } = useNeumorphicTheme();
  const isDark = theme === 'dark';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const mutedTextColor = isDark ? 'text-gray-300' : 'text-gray-600';
  const cardBg = isDark ? 'bg-[#0a0a0a]/50' : 'bg-white/50';
  const borderColor = isDark ? 'border-white/10' : 'border-gray-200';

  useEffect(() => {
    // Preload first 6 images (above the fold) for better performance
    const preloadImages = [...models.slice(0, 6).map(model => model.images.exterior[0] || model.images["2D"][0])];
    preloadImages.forEach(src => {
      if (src) {
        const img = new Image();
        img.src = src;
      }
    });

  }, []);

  return (
    <NeumorphicContainer className="min-h-screen">
      <CanonicalTag url="https://www.udaadu.com/uda-models" />
      <Navigation />
      <NeumorphicThemeToggle />
      
      <main className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl mb-4 text-[#e4683b] md:text-4xl font-normal">
            UDA Models
          </h1>
          <p className={`text-lg ${mutedTextColor} max-w-2xl mx-auto`}>
            Explore our collection of expertly designed ADU models. From cozy studios to spacious family homes, 
            find the perfect design for your property.
          </p>
        </div>

        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl text-brand mb-8 text-center font-normal">
            Our Exclusive Pre-designed Models
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {models.map(model => <ModelBox key={model.id} model={model} />)}
          </div>
        </div>

        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl text-brand mb-8 text-center font-normal">
            Los Altos Preapproved Permit Ready
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {losAltosModels.map(model => <ModelBox key={model.id} model={model} />)}
          </div>
        </div>

        {/* Video Section */}
        <div className="mb-12">
          <div className="w-full max-w-4xl mx-auto">
            <div className={`${cardBg} backdrop-blur-sm border ${borderColor} rounded-xl p-6 overflow-hidden`}>
              <video 
                className="w-full h-auto rounded-lg shadow-lg"
                controls
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                poster=""
              >
                <source 
                  src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/LA%20MEDIAN%20SHORT%20CLIP%20%201%202.mov?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJWSURFT1MvTEEgTUVESUFOIFNIT1JUIENMSVAgIDEgMi5tb3YiLCJpYXQiOjE3NTMxNjUxNzcsImV4cCI6MjA2ODUyNTE3N30.0myJ1OSGYl_GNuDayMKyC3ILlMpdLF0bOyNzfONQQJQ" 
                  type="video/quicktime"
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>

        {/* What Fits My Lot CTA */}
        <div className="mb-12 text-center">
          <h2 className="text-3xl md:text-4xl mb-8 text-[#e4683b] lg:text-4xl font-normal">
            What fits my lot?
          </h2>
          <a 
            href="/what-fits-my-lot"
            className="inline-block px-8 py-4 bg-[#E4683B] text-white rounded-xl text-lg font-semibold hover:opacity-90 transition-opacity"
          >
            Explore Your Property
          </a>
        </div>
      </main>

      <Footer />
    </NeumorphicContainer>
  );
};

const UDAModels = () => {
  return (
    <NeumorphicThemeProvider>
      <UDAModelsContent />
    </NeumorphicThemeProvider>
  );
};

export default UDAModels;
